import type { FieldValues } from 'react-hook-form';

import type { AdditionalConditionField } from '../../edit-field-modal/formRegistryService';

/**
 * Condition sources that are not fields on the form, together with the values
 * the provider resolved for them (CUS-242's T1 Category is the record's nearest
 * Tier-1 ancestor, fetched rather than filled in).
 *
 * `ConditionalFieldsProvider` owns the resolution because only it watches the
 * form values, but the save path in `FormContext` re-evaluates the very same
 * conditions against the submitted payload — and nulls every field it reads as
 * hidden. Evaluating there without these sources reads every rule against one
 * as unmet, so a field the user can see and has just filled in is nulled on the
 * way out. The provider therefore publishes them back up through a ref.
 */
export interface ExternalConditionSources {
  /** Source declarations, for the evaluator's filtering properties. */
  fields?: AdditionalConditionField[];
  /** Resolved values, keyed as the form's own values are. */
  values?: Record<string, unknown>;
}

/**
 * Overlays the externally-resolved values onto a set of form values. Returns
 * the input untouched when there are none, so a form with no external source
 * keeps its own object identity.
 */
export const withExternalConditionValues = (
  currentValues: FieldValues,
  sources: ExternalConditionSources | undefined
): FieldValues =>
  sources?.values ? { ...currentValues, ...sources.values } : currentValues;
